import type { NpcId } from '@/lib/assets';

export interface TutorialReward {
  doubloons?: number;
  marks?: number;
  xp?: number;
  energyMaxBonus?: number;
  title?: string;
  pendingLoot?: { rarity: string; sourceId: string }[];
}

export interface TutorialQuest {
  id: number;
  name: string;
  npc: NpcId;
  introText: string;
  completionText: string;
  objectives: { id: string; description: string }[];
  reward: TutorialReward;
  setTutorialComplete?: boolean;
  setPort?: string;
  nextQuest?: number;
}

export const TUTORIAL_QUESTS: Record<number, TutorialQuest> = {
  2: {
    id: 2,
    name: 'First Blood',
    npc: 'old_pete',
    introText:
      "Bigger arms now, eh? Good. Strength's nothing if you can't put it to use. Mistress Vela runs the Sea Dog Tavern across the way. Tells me she's got a problem with a no-good drunk who keeps stiffing her on his tab. Maybe you could help. Maybe a little persuasion.\n\nThe Nerve Bar at the top of your screen tells you how much daring you've got in you. Crimes cost Nerve. You've got plenty for what we're asking.",
    completionText:
      "I knew you'd handle it. The Sea Dog is the kind of place where you make friends or enemies fast. You did right by Vela. She'll remember that.",
    objectives: [{ id: 'crime_completed', description: "Recover Vela's Coin" }],
    reward: { doubloons: 300, xp: 50 },
    nextQuest: 3,
  },
  3: {
    id: 3,
    name: 'Hold the Line',
    npc: 'drillmaster_bartolo',
    introText:
      "Heard you went and got into a tavern scrap. Lucky for you the drunk could barely stand. Out in real waters, you'll catch a blade or a fist or both. You need Defense. Defense is what keeps the steel from finding your guts.\n\nHead to the Training page and train your Defense with at least 10 Energy. Then come back.",
    completionText:
      "Better. You're not soft anymore, kid. Here — take this old coat and hat. They won't stop a cutlass but they'll slow one down. Now get back out there.",
    objectives: [{ id: 'defense_trained', description: 'Train Defense (10+ Energy)' }],
    reward: {
      doubloons: 250,
      xp: 50,
      pendingLoot: [
        { rarity: 'common', sourceId: 'worn_pirate_coat' },
        { rarity: 'common', sourceId: 'worn_pirate_hat' },
      ],
    },
    nextQuest: 4,
  },
  4: {
    id: 4,
    name: 'Light Feet',
    npc: 'sneaky_jim',
    introText:
      "Psst. Hey. You there. Old Pete's stray. Listen, I run a school here. School of hands-fast and feet-faster. Anyone with a pocket walks past, you take from it. Anyone catches you, you run. That's Agility.\n\nTrain it up first — head to Training and spend at least 10 Energy on Agility. Then come back and I'll show you how it's done in the field.",
    completionText:
      "Better. You move like someone who knows the alley behind every alley. Stick around. There's a job tomorrow if you're keen.",
    objectives: [{ id: 'agility_trained', description: 'Train Agility (10+ Energy)' }],
    reward: { doubloons: 200, xp: 50 },
    nextQuest: 8,
  },
  8: {
    id: 8,
    name: 'The Pickpocket',
    npc: 'sneaky_jim',
    introText:
      "Right, the field test. There's a drunk leaving the tavern about now. His purse is heavy. He'll never see you. But here's the thing — sometimes the marks aren't drunk. Sometimes they catch you. When that happens, you don't get caught. You RUN.\n\nThat's why we trained the Agility, kid. Today, you might fail. That's the point. Watch what happens.",
    completionText:
      "HAH! Did you see the look on his face? Beautiful. You move quick, kid. Keep training that Agility, it'll keep you out of more jails than I can count. Here, take these boots. Worth more than that drunk had on him anyway.",
    objectives: [{ id: 'pickpocket_attempted', description: 'Attempt the tutorial pickpocket' }],
    reward: {
      doubloons: 300,
      xp: 50,
      pendingLoot: [{ rarity: 'common', sourceId: 'worn_pirate_boots' }],
    },
    nextQuest: 9,
  },
  9: {
    id: 9,
    name: 'Cast Off',
    npc: 'harbormaster_finn',
    introText:
      "Word travels fast in Cutlass Cove. Old Pete vouches for you. Vela likes you. Sneaky Jim, well, he doesn't vouch for anyone, but he hasn't run you off either. That's something.\n\nYou've learned what we can teach you here. There's more to learn — about ships, about cargo, about the deeper waters — but those lessons wait for you in Port Royale.\n\nTake this. It's not much, but it'll get you started. Now sail.",
    completionText: 'Permission to depart granted, Captain. Fair winds.',
    objectives: [
      { id: 'graduation_accepted', description: 'Accept graduation and sail to Port Royale' },
    ],
    reward: {
      doubloons: 2000,
      marks: 50,
      xp: 500,
      energyMaxBonus: 20,
      title: 'Greenhorn',
    },
    setTutorialComplete: true,
    setPort: 'port_royale',
    nextQuest: 10,
  },
};

export function getTutorialQuest(id: number): TutorialQuest | undefined {
  return TUTORIAL_QUESTS[id];
}
